import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { track } from '../lib/analytics'
import type { Church, Campus, ChurchMemberRole, OccurrenceWithEvent, ChurchStats, CreateChurchInput, CreateCampusInput, UpdateCampusInput } from '@fellowship/shared'
import { useAuthStore } from '../store/auth'

export function useChurch(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: ['churches', idOrSlug],
    queryFn: () => apiFetch<{ data: Church }>(`/churches/${idOrSlug}`),
    enabled: Boolean(idOrSlug),
    staleTime: 10 * 60 * 1000,
  })
}

export function useChurchEvents(slug: string | undefined) {
  return useQuery({
    queryKey: ['churches', slug, 'events'],
    queryFn: () =>
      apiFetch<{ data: OccurrenceWithEvent[] }>(`/churches/${slug}/events`),
    enabled: Boolean(slug),
    staleTime: 2 * 60 * 1000,
  })
}

export function useFollowStatus(slug: string | undefined) {
  return useQuery({
    queryKey: ['churches', slug, 'follow'],
    queryFn: () => apiFetch<{ data: { following: boolean } }>(`/churches/${slug}/follow`),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
  })
}

export function useFollowChurch(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch(`/churches/${slug}/follow`, { method: 'POST' }),
    onSuccess: () => {
      track('church_followed', { slug })
      void qc.invalidateQueries({ queryKey: ['churches', slug, 'follow'] })
      void qc.invalidateQueries({ queryKey: ['churches', slug] })
    },
  })
}

export function useUnfollowChurch(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch(`/churches/${slug}/follow`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['churches', slug, 'follow'] })
      void qc.invalidateQueries({ queryKey: ['churches', slug] })
    },
  })
}

export function useSearchChurches(params: {
  q?: string
  denomination?: string
  lat?: number
  lng?: number
  radius?: number
  claimedOnly?: boolean
  hasUpcomingEvents?: boolean
}) {
  return useQuery({
    queryKey: ['churches', 'search', params],
    queryFn: () => {
      const sp = new URLSearchParams()
      if (params.q) sp.set('q', params.q)
      if (params.denomination) sp.set('denomination', params.denomination)
      if (params.lat != null) sp.set('lat', String(params.lat))
      if (params.lng != null) sp.set('lng', String(params.lng))
      if (params.radius != null) sp.set('radius', String(params.radius))
      if (params.claimedOnly) sp.set('claimedOnly', 'true')
      if (params.hasUpcomingEvents) sp.set('hasUpcomingEvents', 'true')
      return apiFetch<{ data: Church[]; meta: { total: number } }>(`/churches?${sp}`)
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useChurchStats(slug: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['churches', slug, 'stats'],
    queryFn: () => apiFetch<{ data: ChurchStats }>(`/churches/${slug}/stats`),
    enabled: Boolean(slug) && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useRequestChurch() {
  return useMutation({
    mutationFn: (data: { churchName: string; websiteUrl?: string }) =>
      apiFetch<{ data: { ok: boolean } }>('/church-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

export function useCreateChurch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateChurchInput) =>
      apiFetch<{ data: Church }>('/churches', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['churches'] })
    },
  })
}

export function useClaimChurch(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ data: Church }>(`/churches/${churchId}/claim`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['churches'] })
    },
  })
}

// ─── Campus hooks ─────────────────────────────────────────────────────────────

export function useCampuses(churchId: string | undefined) {
  return useQuery({
    queryKey: ['churches', churchId, 'campuses'],
    queryFn: () => apiFetch<{ data: Campus[] }>(`/churches/${churchId}/campuses`),
    enabled: Boolean(churchId),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateCampus(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCampusInput) =>
      apiFetch<{ data: Campus }>(`/churches/${churchId}/campuses`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['churches', churchId, 'campuses'] })
    },
  })
}

export function useUpdateCampus(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ campusId, data }: { campusId: string; data: UpdateCampusInput }) =>
      apiFetch<{ data: Campus }>(`/churches/${churchId}/campuses/${campusId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['churches', churchId, 'campuses'] })
    },
  })
}

export function useArchiveCampus(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (campusId: string) =>
      apiFetch(`/churches/${churchId}/campuses/${campusId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['churches', churchId, 'campuses'] })
    },
  })
}

export function useTrackEventView(eventId: string | undefined) {
  return useMutation({
    mutationFn: () => apiFetch(`/events/${eventId}/view`, { method: 'POST' }),
  })
}

export function useTrackPlanToAttend(eventId: string | undefined) {
  return useMutation({
    mutationFn: () => apiFetch<{ data: { icsUrl: string } }>(`/events/${eventId}/plan-to-attend`, { method: 'POST' }),
  })
}

export function useInquireChurch(eventId: string | undefined) {
  return useMutation({
    mutationFn: () =>
      apiFetch(`/events/${eventId}/inquire`, { method: 'POST', body: JSON.stringify({}) }),
  })
}

export type UserChurchEntry = {
  id: string
  name: string
  slug: string
  role: ChurchMemberRole
  logoUrl: string | null
}

export interface CalendarImportResult {
  imported: number
  skipped: number
  errors: string[]
}

export function useImportCalendar(churchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (icalUrl: string) =>
      apiFetch<{ data: CalendarImportResult }>(`/churches/${churchId}/calendar/import`, {
        method: 'POST',
        body: JSON.stringify({ icalUrl }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'events'] })
    },
  })
}

export function useMyChurches() {
  const { accessToken } = useAuthStore()
  return useQuery({
    queryKey: ['users', 'me', 'churches'],
    queryFn: () => apiFetch<{ data: UserChurchEntry[] }>('/users/me/churches'),
    enabled: Boolean(accessToken),
    staleTime: 5 * 60 * 1000,
  })
}

export function useChurchDrafts(churchId: string | undefined) {
  const { accessToken } = useAuthStore()
  return useQuery({
    queryKey: ['churches', churchId, 'drafts'],
    queryFn: () => apiFetch<{ data: import('@fellowship/shared').FellowshipEvent[] }>(`/churches/${churchId}/drafts`),
    enabled: Boolean(churchId && accessToken),
    staleTime: 60_000,
  })
}
